import { Card, Checkbox, Combobox, Icon, Input } from '@salesforce/design-system-react';
import { useCallback, useState } from 'react';

import {
  ACCOUNT_COLUMNS,
  accountCellValue,
  CONTACT_COLUMNS,
  contactCellValue,
  type ContactFilters,
  RecordLookup,
  toAccountOption,
  toContactOption,
  useLookupSearch,
} from '../../../shared/components/lookup';
import { IconCategory, type ILookupOption } from '../../../shared/types';
import { LineItemSection } from './LineItemSection';

const PlaceholderSection: React.FC = () => (
  <p className="slds-text-body_small slds-text-color_weak">Contract fields will be added here.</p>
);

/** A single option for a readonly-picklist `Combobox`, matching its `options`/`selection` shape. */
interface PicklistOption {
  id: string;
  label: string;
}

const NONE_OPTION: PicklistOption = { id: '', label: '--None--' };

// Order.Priority__c's picklist values.
const PRIORITY_OPTIONS: PicklistOption[] = [
  { id: 'Standard', label: 'Standard' },
  { id: 'Urgent Truck Waiting', label: 'Urgent Truck Waiting' },
  { id: 'Promised Delivery in 24 Hours', label: 'Promised Delivery in 24 Hours' },
];

// Order.Form__c's picklist values.
const FORM_OPTIONS: PicklistOption[] = [
  { id: 'Standard', label: 'Standard' },
  { id: 'Long', label: 'Long' },
  { id: 'FlgCCStd', label: 'FlgCCStd' },
];

// Order.Concluding_Country__c's picklist values, only relevant for non-US offices.
const CONCLUDING_COUNTRY_OPTIONS: PicklistOption[] = [
  NONE_OPTION,
  { id: 'Australia', label: 'Australia' },
  { id: 'Canada', label: 'Canada' },
  { id: 'China', label: 'China' },
  { id: 'France', label: 'France' },
  { id: 'Singapore', label: 'Singapore' },
  { id: 'Switzerland', label: 'Switzerland' },
  { id: 'United Kingdom', label: 'United Kingdom' },
  { id: 'Uruguay', label: 'Uruguay' },
  { id: 'Philippines', label: 'Philippines' },
  { id: 'Brazil', label: 'Brazil' },
];

/** A display-only field rendered as a plain label and static text, with no input box or border. */
const StaticField: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="slds-form-element">
    <span className="slds-form-element__label">{label}</span>
    <div className="slds-form-element__control">
      <div
        className="slds-form-element__static slds-text-heading_small slds-border_bottom slds-p-bottom_small"
        style={{ display: 'inline-block' }}
      >
        {value}
      </div>
    </div>
  </div>
);

/** A readonly picklist-style field, matching the legacy Order Edit page's `Combobox variant="readonly"` fields. */
const PicklistField: React.FC<{
  label: string;
  onChange: (value: string) => void;
  options: PicklistOption[];
  value: string;
}> = ({ label, onChange, options, value }) => (
  <Combobox
    events={{
      onSelect: (_event, data) => {
        onChange(data.selection[0]?.id ?? '');
      },
    }}
    id={`contract-field-${label}`}
    labels={{ label, placeholder: `Select ${label}` }}
    options={options}
    predefinedOptionsOnly
    selection={[options.find(option => option.id === value) ?? NONE_OPTION]}
    variant="readonly"
  />
);

/**
 * The "Contract" section's fields, matching the legacy `OrderSalesContractSection.cmp` Aura
 * component: Record Type and Pricing Type are display-only; Koch Party, Account, Account Contact
 * (scoped to Account), and Non-Contracted Account are Account/Contact lookups; Priority,
 * Concluding Country, and Form are picklists; Flag to Contract Admins? is a checkbox. The Account
 * Contact lookup is disabled until an Account is selected, and clears its own selection whenever
 * the Account changes so it can't show a Contact that no longer belongs to the selected Account.
 */
const ContractFields: React.FC = () => {
  const { searchAccounts, searchAccountsModal, searchContacts, searchContactsModal } = useLookupSearch();
  const [account, setAccount] = useState<ILookupOption>();
  const [accountContact, setAccountContact] = useState<ILookupOption>();
  const [kochParty, setKochParty] = useState<ILookupOption>();
  const [nonContractedAccount, setNonContractedAccount] = useState<ILookupOption>();
  const [priority, setPriority] = useState('Standard');
  const [concludingCountry, setConcludingCountry] = useState('');
  const [form, setForm] = useState('Standard');
  const [flagToContractAdmins, setFlagToContractAdmins] = useState(false);

  const handleAccountChange = (value: ILookupOption | undefined) => {
    setAccount(value);
    setAccountContact(undefined);
  };

  const searchAccountContacts = useCallback(
    (term: string) => {
      if (!account) throw new Error('An Account is required to search Contacts');
      return searchContacts(term, account.id);
    },
    [account, searchContacts],
  );
  const searchAccountContactsModal = useCallback(
    (filters: ContactFilters, limit: number) => {
      if (!account) throw new Error('An Account is required to search Contacts');
      return searchContactsModal(filters, limit, account.id);
    },
    [account, searchContactsModal],
  );

  return (
    <div className="slds-grid slds-wrap slds-gutters">
      <div className="slds-col slds-size_1-of-1 slds-medium-size_1-of-2 slds-m-bottom_small">
        <StaticField label="Record Type" value="Sales Contract" />
      </div>
      <div className="slds-col slds-size_1-of-1 slds-medium-size_1-of-2 slds-m-bottom_small">
        <StaticField label="Pricing Type" value="Fixed Price" />
      </div>
      <div className="slds-col slds-size_1-of-1 slds-medium-size_1-of-2">
        <RecordLookup
          iconCategory={IconCategory.Standard}
          iconName="account"
          label="Koch Party"
          modal={{
            columns: ACCOUNT_COLUMNS,
            getCellValue: accountCellValue,
            search: searchAccountsModal,
            toOption: toAccountOption,
          }}
          onChange={setKochParty}
          placeholder="Search Accounts…"
          search={searchAccounts}
          value={kochParty}
        />
      </div>
      <div className="slds-col slds-size_1-of-1 slds-medium-size_1-of-2">
        <RecordLookup
          iconCategory={IconCategory.Standard}
          iconName="account"
          label="Account"
          modal={{
            columns: ACCOUNT_COLUMNS,
            getCellValue: accountCellValue,
            search: searchAccountsModal,
            toOption: toAccountOption,
          }}
          onChange={handleAccountChange}
          placeholder="Search Accounts…"
          search={searchAccounts}
          value={account}
        />
      </div>
      <div className="slds-col slds-size_1-of-1 slds-medium-size_1-of-2">
        <PicklistField label="Priority" onChange={setPriority} options={PRIORITY_OPTIONS} value={priority} />
      </div>
      <div className="slds-col slds-size_1-of-1 slds-medium-size_1-of-2">
        <RecordLookup
          disabled={!account}
          iconCategory={IconCategory.Standard}
          iconName="contact"
          label="Account Contact"
          modal={{
            columns: CONTACT_COLUMNS,
            getCellValue: contactCellValue,
            search: searchAccountContactsModal,
            toOption: toContactOption,
          }}
          onChange={setAccountContact}
          placeholder={account ? 'Search Contacts…' : 'Select an Account first'}
          search={searchAccountContacts}
          value={accountContact}
        />
      </div>
      <div className="slds-col slds-size_1-of-1 slds-medium-size_1-of-2">
        <PicklistField
          label="Non-US Offices Only: Concluding Country"
          onChange={setConcludingCountry}
          options={CONCLUDING_COUNTRY_OPTIONS}
          value={concludingCountry}
        />
      </div>
      <div className="slds-col slds-size_1-of-1 slds-medium-size_1-of-2">
        <RecordLookup
          iconCategory={IconCategory.Standard}
          iconName="account"
          label="Non-Contracted Account"
          modal={{
            columns: ACCOUNT_COLUMNS,
            getCellValue: accountCellValue,
            search: searchAccountsModal,
            toOption: toAccountOption,
          }}
          onChange={setNonContractedAccount}
          placeholder="Search Accounts…"
          search={searchAccounts}
          value={nonContractedAccount}
        />
      </div>
      <div className="slds-col slds-size_1-of-1 slds-medium-size_1-of-2">
        <PicklistField label="Form" onChange={setForm} options={FORM_OPTIONS} value={form} />
      </div>
      <div className="slds-col slds-size_1-of-1 slds-medium-size_1-of-2 slds-p-top_small">
        <Checkbox
          checked={flagToContractAdmins}
          labels={{ label: 'Flag to Contract Admins?' }}
          onChange={(_event, data) => {
            setFlagToContractAdmins(data.checked);
          }}
        />
      </div>
    </div>
  );
};

export const ContractDetailsPanel: React.FC = () => (
  <Card
    className="line-item-editor-main slds-card_boundary"
    heading={
      <span className="line-item-editor-contract-heading">
        <Icon
          category={IconCategory.Utility}
          className="line-item-editor-contract-heading-icon"
          name="file"
          size="small"
        />
        <span className="line-item-editor-contract-heading-copy">
          <span className="line-item-editor-contract-title slds-text-heading_small">Contract Details</span>
          <span className="line-item-editor-contract-subtitle slds-text-body_small slds-text-color_weak">
            Update contract-wide information. Line items are managed separately.
          </span>
        </span>
      </span>
    }
  >
    <div className="line-item-editor-sections">
      <LineItemSection defaultOpen iconName="file" summary="Account, dates, status" title="Contract">
        <ContractFields />
      </LineItemSection>
      <LineItemSection iconName="description" summary="Additional terms" title="Details">
        <PlaceholderSection />
      </LineItemSection>
      <LineItemSection iconName="money" summary="Fees and rates" title="Charges">
        <PlaceholderSection />
      </LineItemSection>
      <LineItemSection iconName="edit" summary="Customer-facing notes" title="Notes to Print on Customer Documents">
        <PlaceholderSection />
      </LineItemSection>
      <LineItemSection iconName="info" summary="CA information" title="CA Information">
        <PlaceholderSection />
      </LineItemSection>
      <LineItemSection defaultOpen iconName="money" summary="Payment method" title="Payment Method">
        <div className="slds-grid slds-wrap">
          <div className="slds-size_1-of-1 slds-large-size_1-of-2">
            <Input disabled label="Payment Method" placeholder="Contract field will be added here" />
          </div>
        </div>
      </LineItemSection>
    </div>
  </Card>
);
